import { Dtos, Relations } from "@inrev/common";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useRequest } from "../../../utils/request";
import { adminNoteTopicsQueryKey } from "../note.keys";
import { type AdminNote, AdminNoteTopic } from "./types";

export const useFetchAdminNoteTopics = (relation: Relations.Notes) => {
	const { get } = useRequest();

	const _topics = useQuery({
		queryKey: adminNoteTopicsQueryKey(relation),
		queryFn: async () =>
			await get<Dtos.Admin.AdminNoteTopic.Get.Response[]>(
				`/v2/admin/admin-notes/note-topics?relationType=${relation.relationType}&relationId=${relation.relationId}`,
			),
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		staleTime: 1000 * 60 * 5,
	});

	const sortNotes = (notes: AdminNote[]) => {
		return notes.sort((a, b) => {
			if (a.isPinned && !b.isPinned) return -1;
			if (!a.isPinned && b.isPinned) return 1;
			if (a.isPinned && b.isPinned) {
				if (a.createdAt > b.createdAt) return -1;
				if (a.createdAt < b.createdAt) return 1;
				return 0;
			}
			if (a.createdAt > b.createdAt) return -1;
			if (a.createdAt < b.createdAt) return 1;
			return 0;
		});
	};

	return useMemo(() => {
		const topicsWithoutDefault = (() => {
			const topics = _topics.data
				?.filter((topic) => !topic.isDefault)
				.sort((a, b) => a.name.localeCompare(b.name));
			if (topics !== undefined)
				return topics
					.map((topic) => ({
						...topic,
						notes: sortNotes(topic.notes.filter((note) => !note.isArchived)),
					}))
					.filter((topic) => !topic.isArchived);
		})();
		const defaultTopic = (() => {
			const topic = _topics.data?.find((topic) => topic.isDefault);
			if (topic !== undefined)
				return {
					...topic,
					notes: sortNotes([
						...topic.notes.filter((note) => !note.isArchived),
						...(() => {
							if (topicsWithoutDefault !== undefined) {
								return topicsWithoutDefault.flatMap((topic) =>
									topic.notes.filter((note) => note.isPinned),
								);
							}
							return [];
						})(),
					]),
				};
		})();
		const defaultArchivedTopic = (() => {
			const topic = _topics.data?.find((topic) => topic.isDefault);
			if (topic !== undefined)
				return {
					...topic,
					notes: sortNotes(topic.notes.filter((note) => note.isArchived)),
				};
		})();
		const archivedTopics = (() => {
			const topics = _topics.data
				?.filter((topic) => !topic.isDefault)
				.sort((a, b) => a.name.localeCompare(b.name));
			if (topics !== undefined)
				return topics.reduce<AdminNoteTopic[]>((acc, topic) => {
					if (topic.isArchived) {
						acc.push({ ...topic, notes: sortNotes(topic.notes) });
					}
					if (topic.notes.some((note) => note.isArchived && !topic.isArchived)) {
						acc.push({
							...topic,
							notes: sortNotes(topic.notes.filter((note) => note.isArchived)),
						});
					}
					return acc;
				}, []);
		})();

		return {
			defaultTopic,
			topics: topicsWithoutDefault,
			defaultArchivedTopic,
			archivedTopics,
			adminNoteTopicsIsLoading: _topics.isLoading,
			adminNoteTopicsError: _topics.error,
		};
	}, [_topics]);
};
